// The plan strip above every signed-in page: the offer, how much of the period's pool is used, and the way to
// more (the full bar and the member lines stay on /usage). Admins on Free or a trial, or near the end of the
// pool, get "Upgrade" to the billing page. A plain member sees the offer and only their own spend against their
// limit, never the pool nor the credits.
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { UsageSummary } from '../types';
import { date, money, ownUsageOnly, usageRatio } from './format';

export function PlanStrip({ usage, admin }: { usage: UsageSummary; admin: boolean }) {
  const { t, i18n } = useTranslation();
  if (!usage.offer) return null; // no subscription: the billing banner says what to do
  const locale = i18n.language;
  const c = usage.currency;
  const own = !admin || ownUsageOnly(usage, null);
  // A member's figures: their spend against their limit; no limit reads as unlimited.
  const spent = own ? usage.user_spent : usage.spent_usd;
  const budget = own ? (usage.member_cap ?? 0) : usage.budget_usd;
  const unlimited = own ? usage.member_cap === null : usage.unlimited;
  const ratio = usageRatio(spent, budget, unlimited);
  const entry = usage.period === 'trial' || usage.free;
  const upgrade = admin && !own && (entry || usage.limit_reached || ratio >= 0.8);
  const tone = usage.limit_reached ? 'bg-danger' : ratio >= 0.8 ? 'bg-warn' : 'bg-accent';
  return (
    <section className="rounded-lg border border-line-soft bg-white px-4 py-3" data-testid="plan-strip">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="flex items-baseline gap-2">
          <span className="text-[13px] font-medium text-ink">{usage.offer}</span>
          {usage.period === 'trial' && <span className="font-mono text-[10.5px] uppercase tracking-[0.06em] text-faint">{t('plan.trial')}</span>}
          <span className="text-[13px] text-muted">
            {own && <span className="text-faint">{t('usage.own.title')} · </span>}
            {unlimited ? t('usage.spentUnlimited', { spent: money(spent, c, locale) }) : t('usage.spent', { spent: money(spent, c, locale), allowance: money(budget, c, locale) })}
          </span>
        </span>
        <span className="flex items-baseline gap-3 text-[12px]">
          {usage.period_end && <span className="font-mono text-[11px] text-faint">{t(usage.period === 'trial' ? 'plan.trialEnds' : 'plan.renews', { date: date(usage.period_end, locale) })}</span>}
          <Link to="/usage" className="text-muted underline hover:text-ink">
            {t('plan.details')}
          </Link>
          {upgrade && (
            <Link to="/billing" className="font-medium text-accent-dark underline hover:text-ink" data-testid="plan-upgrade">
              {t('plan.upgrade')}
            </Link>
          )}
        </span>
      </div>
      {!unlimited && (
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-hover" role="progressbar" aria-label={t(own ? 'usage.own.bar' : 'plan.bar')} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(ratio * 100)}>
          <div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.round(ratio * 100)}%` }} />
        </div>
      )}
      {!own && usage.credits > 0 && <p className="mt-1.5 text-[12px] text-faint">{t('plan.credits', { credits: money(usage.credits, c, locale) })}</p>}
    </section>
  );
}
